import React, {
  useState, useEffect, useContext,
} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PropTypes from 'prop-types';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { NotificationContext } from '../../contexts/Notification';
import { UserContext } from '../../contexts/User';
import { MemberSelector } from '../../components/selectors/MemberNameSelector';
import erroimage from '../../images/error_outline.svg';
import { TeamSelector } from '../../components/selectors/TeamSelector';

function ProjectEditModal({
  open, onClose, onSubmit, loading = false, projectData = null,
}) {
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    billable: true,
    disableActivity: false,
    disableIdleTime: false,
    client: '',
    managers: [],
    members: [],
    viewers: [],
    teams: '',
  });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const { displayNotification } = useContext(NotificationContext);
  const { activeWorkspace } = useContext(UserContext);
  const [workspaceMembers, setWorkspaceMembers] = useState([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);

  const [workspaceTeams, setWorkspaceTeams] = useState([]);
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);
  const [isLoadingProjectTeams, setIsLoadingProjectTeams] = useState(false);

  const fetchMembers = async () => {
    setIsLoadingMembers(true);
    try {
      const { success, data } = await sendRequest({
        method: 'get',
        url: API.WORKSPACE.members(activeWorkspace.id),
        params: {
          pageSize: 0,
        },
      });

      if (success) {
        const filteredMembers = data.members
          .filter(({ haveJoined }) => haveJoined)
          .map((item) => ({
            id: item.id,
            name: `${item.user.firstName} ${item.user.lastName}`,
          }));
        filteredMembers.sort((item1, item2) => {
          if (item1.name > item2.name) return 1;
          if (item1.name < item2.name) return -1;
          return 0;
        });
        setWorkspaceMembers(filteredMembers);
      } else {
        displayNotification({
          message: 'There was an issue fetching members in this workspace. Please try again',
          severity: 'error',
        });
      }
    } catch (error) {
      displayNotification({
        message: 'Error fetching workspace members',
        severity: 'error',
      });
    }
    setIsLoadingMembers(false);
  };

  const fetchProjectMembers = async () => {
    if (!projectData?.id) {
      return;
    }

    setIsLoadingMembers(true);
    try {
      const response = await sendRequest({
        method: 'get',
        url: API.WORKSPACE.getProjectMembers(activeWorkspace.id, projectData.id),
        params: {
          pageSize: 0,
        },
      });

      console.log(' FULL RESPONSE:', response);
      console.log(' response.data:', response.data);
      console.log(' response.data type:', typeof response.data);
      console.log(' response.data is array:', Array.isArray(response.data));

      const dataArray = response?.data?.data || response?.data || response;

      console.log(' dataArray:', dataArray);
      console.log(' dataArray is array:', Array.isArray(dataArray));

      if (Array.isArray(dataArray)) {
        console.log('Found array, proceeding with forEach');

        const managers = [];
        const membersList = [];
        const viewers = [];

        dataArray.forEach((member) => {
          const memberData = {
            id: member.id,
            name: member.name,
            email: member.email,
          };

          if (member.isManager) {
            managers.push(memberData);
          } else if (member.isViewer) {
            viewers.push(memberData);
          } else {
            membersList.push(memberData);
          }
        });

        setFormData((prev) => ({
          ...prev,
          managers,
          members: membersList,
          viewers,
        }));

        console.log(' Set form data:', { managers, members: membersList, viewers });
      } else {
        console.log(' Still not an array!');
      }
    } catch (error) {
      console.error('Error fetching project members:', error);
    }
    setIsLoadingMembers(false);
  };

  const fetchTeams = async () => {
    setIsLoadingTeams(true);
    try {
      const { success, data } = await sendRequest({
        method: 'get',
        url: API.WORKSPACE.getWorkspaceTeams(activeWorkspace.id),
        params: {
          pageSize: 0,
        },
      });

      if (success) {
        const filteredTeams = data.formattedTeams
          .map((item) => ({
            id: item.id,
            name: item.name,
          }));
        filteredTeams.sort((item1, item2) => {
          if (item1.name > item2.name) return 1;
          if (item1.name < item2.name) return -1;
          return 0;
        });
        setWorkspaceTeams(filteredTeams);
      } else {
        displayNotification({
          message: 'There was an issue fetching teams in this workspace. Please try again',
          severity: 'error',
        });
      }
    } catch (error) {
      displayNotification({
        message: 'Error fetching workspace teams',
        severity: 'error',
      });
    }
    setIsLoadingTeams(false);
  };

  const fetchProjectTeams = async () => {
    if (!projectData?.id) return;

    setIsLoadingProjectTeams(true);
    try {
      const response = await sendRequest({
        method: 'get',
        url: API.WORKSPACE.getProjectTeams(activeWorkspace.id, projectData.id),
      });

      console.log('Project teams response:', response);

      if (response.success && response.data) {
        const projectTeams = Array.isArray(response.data.data) ? response.data.data : [];

        console.log('Setting project teams as array:', projectTeams);
        setFormData((prev) => ({
          ...prev,
          teams: projectTeams,
        }));
      } else {
        console.log('No teams data, setting empty array');
        setFormData((prev) => ({
          ...prev,
          teams: [],
        }));
      }
    } catch (error) {
      console.error('Error fetching project teams:', error);
      setFormData((prev) => ({
        ...prev,
        teams: [],
      }));
    }
    setIsLoadingProjectTeams(false);
  };

  const handleSelectAllTeams = () => {
    setFormData({
      ...formData,
      teams: [...workspaceTeams],
    });
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  useEffect(() => {
    if (open && activeWorkspace?.id) {
      fetchMembers();
      fetchTeams();
    }
  }, [open, activeWorkspace?.id]);

  useEffect(() => {
    if (projectData && open) {
      console.log('Setting up initial form data for project:', projectData.id);
      setFormData({
        name: projectData.name || '',
        description: projectData.description || '',
        billable: projectData.billable === 1 || projectData.billable === true,
        disableActivity: projectData.activity === 0 || projectData.activity === false,
        disableIdleTime: projectData.idleTime === 0 || projectData.idleTime === false,
        client: projectData.client || '',
        managers: [],
        members: [],
        viewers: [],
        teams: [],
      });

      setErrors({});
      setSubmitError('');

      if (projectData.id) {
        console.log('----------------------projectData.id-----', projectData.id);
        fetchProjectMembers(projectData.id);
        fetchProjectTeams(projectData.id);
      }
    }
  }, [projectData, open]);

  const handleInputChange = (field) => (event) => {
    const { value, checked, type } = event.target;
    setFormData((prev) => ({
      ...prev,
      [field]: type === 'checkbox' ? checked : value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleSelectAllManagers = () => {
    if (Array.isArray(workspaceMembers)) {
      setFormData({
        ...formData,
        managers: workspaceMembers.filter((member) => !formData.members.some((m) => m.id === member.id)
            && !formData.viewers.some((v) => v.id === member.id)),
      });
    }
  };

  const handleSelectAllMembers = () => {
    if (Array.isArray(workspaceMembers)) {
      setFormData({
        ...formData,
        members: workspaceMembers.filter((member) => !formData.managers.some((m) => m.id === member.id)
            && !formData.viewers.some((v) => v.id === member.id)),
      });
    }
  };

  const handleSelectAllViewers = () => {
    if (Array.isArray(workspaceMembers)) {
      setFormData({
        ...formData,
        viewers: workspaceMembers.filter((member) => !formData.managers.some((m) => m.id === member.id)
            && !formData.members.some((m) => m.id === member.id)),
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name || formData.name.trim().length === 0) {
      newErrors.name = 'Project name is required';
    } else if (formData.name.trim().length < 3) {
      newErrors.name = 'Project name must be at least 3 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      billable: true,
      disableActivity: false,
      disableIdleTime: false,
      client: '',
      managers: [],
      members: [],
      viewers: [],
      teams: [],
    });
    setErrors({});
    setSubmitError('');
    setIsLoadingProjectTeams(false);
    setActiveTab(0);
    onClose();
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitError('');

    try {
      const updatedProjectData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        billable: formData.billable,
        activity: !formData.disableActivity,
        idleTime: !formData.disableIdleTime,
        client: formData.client,
        isArchived: projectData?.isArchived || false,
        createdAt: projectData?.createdAt,
        updatedAt: new Date().toISOString(),
        id: projectData?.id,
        members: {
          managers: formData.managers,
          members: formData.members,
          viewers: formData.viewers,
        },
        teams: formData.teams,
      };

      console.log('Updating project with data:', updatedProjectData);

      await onSubmit(updatedProjectData);

      displayNotification({
        message: 'Project updated successfully!',
        severity: 'success',
      });

      handleClose();
    } catch (error) {
      console.error('Error updating project:', error);
      setSubmitError(error.message || 'Failed to update project');
    }
  };

  const renderGeneralTab = () => (
    <Box sx={{
      display: 'flex', flexDirection: 'column', gap: 3, pt: 2,
    }}
    >
      {submitError && (
        <Alert severity="error">{submitError}</Alert>
      )}

      <Box>
        <Box sx={{
          mb: 1, fontSize: '14px !important', fontWeight: '500 !important', color: '#333 !important',
        }}
        >
          Project Name
        </Box>
        <TextField
          fullWidth
          placeholder="Required"
          value={formData.name}
          onChange={handleInputChange('name')}
          error={!!errors.name}
          helperText={errors.name}
          disabled={loading}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#f8f9fa',
              fontSize: '14px !important',
              '& fieldset': {
                borderColor: '#e0e0e0',
              },
              '& input': {
                fontSize: '14px !important',
              },
            },
          }}
        />
      </Box>

      <Box>
        <FormControlLabel
          control={(
            <Switch
              checked={formData.billable}
              onChange={handleInputChange('billable')}
              disabled={loading}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#4469A1 !important',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#4469A1 !important',
                },
              }}
            />
          )}
          label="Billable"
          sx={{
            '& .MuiFormControlLabel-label': {
              fontSize: '14px !important',
              fontWeight: '500 !important',
              color: '#333 !important',
            },
          }}
        />
      </Box>

      <Box>
        <FormControlLabel
          control={(
            <Switch
              checked={formData.disableActivity}
              onChange={handleInputChange('disableActivity')}
              disabled={loading}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#4469A1 !important',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#4469A1 !important',
                },
              }}
            />
          )}
          label="Disable Activity"
          sx={{
            '& .MuiFormControlLabel-label': {
              fontSize: '14px !important',
              fontWeight: '500 !important',
              color: '#333 !important',
            },
          }}
        />
      </Box>

      <Box>
        <FormControlLabel
          control={(
            <Switch
              checked={formData.disableIdleTime}
              onChange={handleInputChange('disableIdleTime')}
              disabled={loading}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#4469A1 !important',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#4469A1 !important',
                },
              }}
            />
          )}
          label="Disable Idle Time"
          sx={{
            '& .MuiFormControlLabel-label': {
              fontSize: '14px !important',
              fontWeight: '500 !important',
              color: '#333 !important',
            },
          }}
        />
      </Box>

      <Box>
        <Box sx={{
          mb: 1, fontSize: '14px !important', fontWeight: '500 !important', color: '#333 !important',
        }}
        >
          Client
        </Box>
        <FormControl fullWidth>
          <Select
            value={formData.client}
            onChange={handleInputChange('client')}
            displayEmpty
            disabled={loading}
            sx={{
              backgroundColor: '#f8f9fa',
              fontSize: '14px !important',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#e0e0e0',
              },
              '& .MuiSelect-select': {
                fontSize: '14px !important',
              },
            }}
          >
            <MenuItem value="" sx={{ fontSize: '14px !important', color: '#999 !important' }}>
              Optional
            </MenuItem>
            <MenuItem value="client1" sx={{ fontSize: '14px !important' }}>Client 1</MenuItem>
            <MenuItem value="client2" sx={{ fontSize: '14px !important' }}>Client 2</MenuItem>
          </Select>
        </FormControl>
      </Box>
    </Box>
  );

  const renderMembersTab = () => (
    <Box sx={{
      display: 'flex', flexDirection: 'column', gap: 3, pt: 2,
    }}
    >
      <Box sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        padding: '12px',
        backgroundColor: '#f8f9ff !important',
        border: '1px solid #d6d9ff !important',
        borderRadius: '4px',
      }}
      >
        <Box>
          <img
            src={erroimage}
            alt="Error Icon"
            width="16"
            height="16"
          />
        </Box>
        <Box sx={{ fontSize: '13px !important', color: '#4a5568 !important', lineHeight: '1.4 !important' }}>
          Moving Project viewers to Users or Managers will move them from a free seat to a paid seat,
          and may increase the number of paid seats on your plan.
        </Box>
      </Box>

      {isLoadingMembers ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
          <Box sx={{ ml: 2, fontSize: '14px', color: '#666' }}>Loading workspace members...</Box>
        </Box>
      ) : (
        <>
          <MemberSelector
            label="Managers"
            selectedMembers={formData.managers || []}
            availableMembers={Array.isArray(workspaceMembers) ? workspaceMembers
              .filter((member) => !formData.members.some((m) => m.id === member.id)
                && !formData.viewers.some((v) => v.id === member.id)) : []}
            onChange={(selectedMembers) => {
              setFormData((prev) => ({
                ...prev,
                managers: selectedMembers,
              }));
            }}
            onSelectAll={handleSelectAllManagers}
          />

          <MemberSelector
            label="Members"
            selectedMembers={formData.members || []}
            availableMembers={Array.isArray(workspaceMembers) ? workspaceMembers
              .filter((member) => !formData.managers.some((m) => m.id === member.id)
                && !formData.viewers.some((v) => v.id === member.id)) : []}
            onChange={(selectedMembers) => {
              setFormData((prev) => ({
                ...prev,
                members: selectedMembers,
              }));
            }}
            onSelectAll={handleSelectAllMembers}
          />

          <MemberSelector
            label="Viewers"
            selectedMembers={formData.viewers || []}
            availableMembers={Array.isArray(workspaceMembers) ? workspaceMembers
              .filter((member) => !formData.managers.some((m) => m.id === member.id)
                && !formData.members.some((m) => m.id === member.id)) : []}
            onChange={(selectedMembers) => {
              setFormData((prev) => ({
                ...prev,
                viewers: selectedMembers,
              }));
            }}
            onSelectAll={handleSelectAllViewers}
          />
        </>
      )}
    </Box>
  );

  const renderTeamsTab = () => (
    <Box sx={{
      display: 'flex', flexDirection: 'column', gap: 3, pt: 2,
    }}
    >
      {(isLoadingTeams || isLoadingProjectTeams) ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
          <Box sx={{ ml: 2, fontSize: '14px', color: '#666' }}>
            {isLoadingTeams ? 'Loading workspace teams...' : 'Loading project teams...'}
          </Box>
        </Box>
      ) : (
        <TeamSelector
          label="Teams"
          selectedTeams={Array.isArray(formData.teams) ? formData.teams : []}
          availableTeams={workspaceTeams}
          onChange={(selectedTeams) => {
            setFormData((prev) => ({
              ...prev,
              teams: selectedTeams,
            }));
          }}
          onSelectAll={handleSelectAllTeams}
        />
      )}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '8px !important',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15) !important',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '18px !important',
          fontWeight: '600 !important',
          color: '#333 !important',
          borderBottom: '1px solid #e0e0e0',
          pb: 2,
        }}
      >
        Edit Project
        <IconButton
          onClick={handleClose}
          disabled={loading}
          sx={{
            color: '#666 !important',
            '&:hover': {
              backgroundColor: '#f5f5f5 !important',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            borderBottom: '1px solid #e0e0e0',
            '& .MuiTab-root': {
              fontSize: '14px !important',
              fontWeight: '500 !important',
              textTransform: 'none !important',
              color: '#666 !important',
              '&.Mui-selected': {
                color: '#315285 !important',
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#315285 !important',
            },
          }}
        >
          <Tab label="General" />
          <Tab label="Members" />
          <Tab label="Teams" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {activeTab === 0 && renderGeneralTab()}
          {activeTab === 1 && renderMembersTab()}
          {activeTab === 2 && renderTeamsTab()}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          pt: 2,
          borderTop: '1px solid #e0e0e0',
          gap: 2,
        }}
      >
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{
            color: '#666 !important',
            fontSize: '14px !important',
            textTransform: 'none',
            padding: '8px 20px',
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          variant="contained"
          sx={{
            backgroundColor: '#53A551 !important',
            fontSize: '14px !important',
            fontWeight: '500 !important',
            textTransform: 'none !important',
            minWidth: '100px',
            '&:hover': {
              backgroundColor: '#53A551 !important',
            },
            '&:disabled': {
              backgroundColor: '#ccc !important',
              color: '#999 !important',
            },
          }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ProjectEditModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  projectData: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    billable: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    activity: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    idleTime: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    client: PropTypes.string,
    members: PropTypes.shape({
      managers: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        email: PropTypes.string,
      })),
      members: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        email: PropTypes.string,
      })),
      viewers: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        email: PropTypes.string,
      })),
    }),
    teams: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string,
    })),
    isArchived: PropTypes.bool,
    createdAt: PropTypes.string,
  }),
};

export default ProjectEditModal;
